import { Button, Form, Input } from "antd";
import Countdown from "antd/es/statistic/Countdown";
import React, { ReactNode, useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import arrowBack from "../../../../assets/images/arrow-left.svg";
import { FormValuesOtp } from "../../../../core/models/custom/formValuesOtp";
import { validateOtpReset } from "../../../../core/services/answerService/validateOtp/actions";
import { ValidateOtpState } from "../../../../core/services/answerService/validateOtp/types";
import FirebaseService from "../../../../core/services/firebaseService/firebase.service";
import { Errors } from "../../../../core/utilities/errors";
import { IStore } from "../../../../core/utilities/reducers";
import FirebaseKey from "../../../FirebaseKey/FirebaseKey";
import Loading from "../../../Loading/Loading";
import "./OtpForm.scss";

interface IProps {
  isLoading: boolean;
  callbackSubmit: (values?: FormValuesOtp) => void;
  callbackNewCode: () => void;
  validateOtpState: ValidateOtpState;
  validateOtpReset: () => void;
}

const OtpForm = (props: IProps) => {
  const [countdown, setCountdown] = useState(Date.now() + 181 * 1000);
  const [isNewCodeVisible, setIsNewCodeVisible] = useState(false);
  const [isNewCodeDisabled, setIsNewCodeDisabled] = useState(true);
  const [otpLengthError, setOtpLengthError] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();
  const otpLength = 4;

  useEffect(() => {
    return () => props.validateOtpReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restartCounter = (): void => {
    setCountdown(Date.now() + 181 * 1000);
    setIsNewCodeVisible(false);
    setIsNewCodeDisabled(true);
  };

  const handleNewCode = (): void => {
    props.callbackNewCode();
    restartCounter();
  };

  const handleFinishCountdown = (): void => {
    setIsNewCodeVisible(true);
    setIsNewCodeDisabled(true);
    setTimeout(() => {
      setIsNewCodeDisabled(false);
    }, 10000);
  };

  function isOtpInvalidError(): boolean {
    return (
      !!props.validateOtpState.error &&
      Errors.isOtpInvalidError(props.validateOtpState.error)
    );
  }

  function isOtpGeneralError(): boolean {
    return (
      !!props.validateOtpState.error &&
      Errors.isOtpGeneralError(props.validateOtpState.error)
    );
  }

  function isOtpBalanceError(): boolean {
    return (
      !!props.validateOtpState.error &&
      Errors.isOtpBalanceError(props.validateOtpState.error)
    );
  }

  function getOtpInputClass(): string {
    const otp = form.getFieldValue("otp") ?? "";
    let className = "otp-input";
    if (
      isSubmitted &&
      (otpLengthError ||
        isOtpInvalidError() ||
        isOtpGeneralError() ||
        isOtpBalanceError())
    ) {
      className += " error";
    } else if (otp.length === otpLength) {
      className += " full";
    }
    return className;
  }

  function handleOtpKeyPress(e: React.KeyboardEvent): void {
    if (isNaN(parseInt(e.key))) {
      e.preventDefault();
    }
  }

  function handleOtpChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const otpValue = e.target.value;
    setOtpLengthError(otpValue.length !== otpLength);
  }

  function handleFinish(values: FormValuesOtp): void {
    setSubmitted(true);
    props.callbackSubmit(values);
  }

  function handleFinishFailed(): void {
    setOtpLengthError(true);
    setSubmitted(true);
  }

  function renderOtpNote(): ReactNode {
    let noteKey;
    if (isOtpInvalidError()) {
      noteKey = FirebaseService.otp_error_message;
    } else if (isOtpGeneralError()) {
      noteKey = FirebaseService.otp_general_error_message;
    } else if (isOtpBalanceError()) {
      noteKey = FirebaseService.otp_balance_error_message;
    } else if (otpLengthError) {
      noteKey = FirebaseService.otp_error_message;
    }
    return noteKey ? (
      <FirebaseKey className="otp-error" firebaseKey={noteKey} />
    ) : (
      <React.Fragment />
    );
  }

  function renderCounter(): ReactNode {
    if (isNewCodeVisible) {
      return (
        <Button
          type="link"
          className="link-button"
          disabled={isNewCodeDisabled}
          onClick={handleNewCode}
        >
          YENİ KOD
        </Button>
      );
    }
    return (
      <Countdown
        className="countdown"
        format="mm:ss"
        value={countdown}
        onFinish={handleFinishCountdown}
      />
    );
  }

  function renderButtons(): ReactNode {
    return (
      <div className="buttons">
        <Button
          className="app-button secondary"
          disabled={props.isLoading}
          onClick={() => props.callbackSubmit(undefined)}
        >
          <img src={arrowBack} alt="arrow" />
        </Button>
        <Button
          className="app-button"
          htmlType="submit"
          disabled={props.isLoading}
        >
          {props.isLoading ? (
            <Loading fontSize={20} />
          ) : (
            <FirebaseKey
              firebaseKey={FirebaseService.mobile_payment_otp_popup_button}
            />
          )}
        </Button>
      </div>
    );
  }

  return (
    <Form
      form={form}
      id="otp-form"
      className="app-form"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <Form.Item
        name="otp"
        rules={[{ required: true, message: "", len: otpLength }]}
      >
        <Input
          className={getOtpInputClass()}
          autoFocus
          type="tel"
          maxLength={otpLength}
          max={9999}
          placeholder="****"
          onKeyPress={handleOtpKeyPress}
          onChange={handleOtpChange}
        />
      </Form.Item>
      {renderOtpNote()}
      {renderCounter()}
      {renderButtons()}
    </Form>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators(
    {
      validateOtpReset,
    },
    dispatch
  );
};
const mapStateToProps = (store: IStore) => {
  return {
    validateOtpState: store.validateOtp,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OtpForm);
